import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { REVIEWS, CAROUSEL_IMAGES, cdnImg } from "../lib/data";

// Decorative food polaroids floating around the reviews (desktop).
// Stickier composition: top-left fries now close to bottom-left burgers,
// balanced with right-side photos. Each has its own parallax depth + rotation.
// Mobile uses a different inline strategy — polaroids are placed in the gaps
// between cards so they are fully visible instead of hidden behind cards.
// Mobile polaroids: a pair at the top above the first card, and a matching
// pair at the bottom after the last card. No inline polaroids between cards
// — keeps the mobile reviews list clean and uncluttered.
const DECOR_MOBILE_PAIRS = {
    top: {
        left: { img: 0, rotate: -8, tape: "ORDER", width: 150 },
        right: { img: 4, rotate: 7, tape: "FULLY LICENSED", width: 160 },
    },
    bottom: {
        left: { img: 3, rotate: -9, tape: "NEW", width: 155 },
        right: { img: 1, rotate: 6, tape: "FAN FAV", width: 165 },
    },
};

const DECOR = [
    {
        img: 0, // loaded fries (top-left, close to burgers below)
        top: "18%",
        left: "1%",
        width: 230,
        rotate: -7,
        delay: 0,
        parallax: -28,
        tape: "ORDER",
    },
    {
        img: 1, // double burgers (bottom-left)
        bottom: "-2%",
        left: "5%",
        width: 260,
        rotate: 5,
        delay: 0.3,
        parallax: 22,
        tape: "FAN FAV",
    },
    {
        img: 4, // neon bar (top-right)
        top: "6%",
        right: "1%",
        width: 280,
        rotate: 6,
        delay: 0.15,
        parallax: -42,
        tape: "FULLY LICENSED",
    },
    {
        img: 3, // chicken tenders (bottom-right)
        bottom: "4%",
        right: "5%",
        width: 220,
        rotate: -10,
        delay: 0.45,
        parallax: 36,
        tape: "NEW",
    },
    {
        img: 2, // shake (mid-left, smaller)
        top: "52%",
        left: "-3%",
        width: 170,
        rotate: 14,
        delay: 0.6,
        parallax: -18,
        tape: null,
    },
    {
        img: 5, // egg burger (mid-right, smaller)
        top: "46%",
        right: "-2%",
        width: 195,
        rotate: -8,
        delay: 0.75,
        parallax: 14,
        tape: null,
    },
];

export default function Reviews({ compact = false }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    return (
        <section
            ref={ref}
            id="reviews"
            data-testid="reviews-section"
            className="relative py-16 md:py-32 bg-[#F4F0E9] md:bg-[#FAF8F4] overflow-hidden"
        >
            {/* DESKTOP-ONLY floating food polaroid collage */}
            {!compact && (
                <div
                    aria-hidden="true"
                    className="hidden md:block absolute inset-0 pointer-events-none"
                >
                    {DECOR.map((d, i) => (
                        <Polaroid
                            key={i}
                            d={d}
                            i={i}
                            scrollYProgress={scrollYProgress}
                        />
                    ))}
                </div>
            )}

            {/* Float keyframes removed — they caused jitter when compounded
                with scroll-driven parallax on rotated polaroid cards. */}

            {/* MOBILE-ONLY inline polaroids now rendered between the review cards below */}

            {/* Sticky "★ Loved by Albany ★" ribbon behind the heading */}
            {!compact && (
                <div
                    aria-hidden="true"
                    className="absolute left-0 right-0 top-[22%] md:top-[28%] overflow-hidden pointer-events-none select-none"
                >
                    <div className="font-display uppercase text-[#EB4A18]/[0.06] text-[clamp(5rem,18vw,16rem)] leading-none tracking-tight whitespace-nowrap text-center">
                        ★ Loved by Albany ★ Loved by Albany ★
                    </div>
                </div>
            )}

            <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                {/* Heading (hidden in compact mode) */}
                {!compact && (
                    <div className="text-center mb-12 md:mb-20">
                        <span
                            aria-hidden="true"
                            className="block mx-auto mb-6 h-px w-16 bg-black/25"
                        />
                        <h2 className="font-display uppercase text-[#121212] text-[clamp(2.2rem,5.5vw,4.5rem)] leading-[0.95]">
                            Loved
                            <span className="text-[#EB4A18]">.</span> by Albany
                            <span className="text-[#EB4A18]">.</span>
                        </h2>
                        <div className="mt-6 inline-flex items-center gap-3">
                            <Stars />
                            <span className="overline text-[#121212]">
                                4.5 · 168 Google reviews
                            </span>
                        </div>
                    </div>
                )}

                {/* DESKTOP — staggered review cards */}
                <div className="hidden md:grid grid-cols-3 gap-7 lg:gap-9">
                    {REVIEWS.map((r, idx) => (
                        <motion.figure
                            key={`d-${r.author}`}
                            data-testid={`review-card-${idx}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{
                                duration: 0.9,
                                delay: idx * 0.12,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className={`relative bg-white p-10 lg:p-12 border-t-4 border-[#EB4A18] shadow-[0_28px_60px_-30px_rgba(0,0,0,0.25)] ${
                                idx === 1 ? "lg:-translate-y-6" : ""
                            }`}
                        >
                            <span
                                aria-hidden="true"
                                className="absolute -top-6 right-6 font-display text-[7rem] leading-none text-[#EB4A18]/15 select-none"
                            >
                                ”
                            </span>
                            <div className="flex items-center gap-1 mb-6">
                                <Stars />
                            </div>
                            <blockquote className="text-[#121212] text-lg leading-relaxed">
                                {r.quote}
                            </blockquote>
                            <figcaption className="mt-8 pt-6 border-t border-black/10 flex items-center justify-between gap-3">
                                <span className="font-display uppercase text-xl text-[#121212]">
                                    {r.author}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.22em] font-semibold text-[#6B6B6B] border border-black/10 px-2.5 py-1">
                                    <GoogleG /> Verified
                                </span>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>

                {/* Footer CTA (desktop only) — sits above the floating photos */}
                {!compact && (
                    <div className="hidden md:flex justify-center items-center gap-4 mt-16 lg:mt-24">
                        <Link
                            to="/book"
                            data-testid="reviews-book-table-btn"
                            className="btn-primary text-sm px-9 py-4 hover:!bg-[#EB4A18]"
                        >
                            Book a Table
                            <span aria-hidden="true">→</span>
                        </Link>
                        <Link
                            to="/vouchers"
                            data-testid="reviews-gift-voucher-btn"
                            className="text-sm px-9 py-4 border border-[#121212] text-[#121212] uppercase tracking-[0.18em] font-medium hover:bg-[#121212] hover:text-[#FAF8F4] transition-colors inline-flex items-center gap-2"
                        >
                            Gift a Voucher
                            <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                )}

                {/* MOBILE — top pair + stacked cards + bottom pair */}
                <div className="md:hidden relative z-10">
                    {/* Polaroid pair above the first card */}
                    {!compact && (
                        <InlinePair pair={DECOR_MOBILE_PAIRS.top} />
                    )}

                    <div className="space-y-5">
                        {REVIEWS.map((r, idx) => (
                            <motion.figure
                                key={`m-${r.author}`}
                                data-testid={`review-card-mobile-${idx}`}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.25 }}
                                transition={{
                                    duration: 0.7,
                                    delay: idx * 0.08,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className="relative bg-white border-l-4 border-[#EB4A18] p-5 pl-6 shadow-[0_18px_40px_-22px_rgba(0,0,0,0.35)]"
                            >
                                <span
                                    aria-hidden="true"
                                    className="absolute top-2 right-4 font-display text-[5rem] leading-none text-[#EB4A18]/15 select-none"
                                >
                                    ”
                                </span>
                                <div className="flex items-center gap-1 mb-3">
                                    <Stars small />
                                </div>
                                <blockquote className="text-[#121212] text-[0.95rem] leading-relaxed">
                                    {r.quote}
                                </blockquote>
                                <figcaption className="mt-4 flex items-center justify-between gap-3">
                                    <span className="font-display uppercase text-lg text-[#121212]">
                                        {r.author}
                                    </span>
                                    <span className="overline text-[0.65rem] text-[#6B6B6B]">
                                        Google review
                                    </span>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    {/* Polaroid pair below the last card */}
                    {!compact && (
                        <div className="mt-2">
                            <InlinePair pair={DECOR_MOBILE_PAIRS.bottom} />
                        </div>
                    )}
                </div>

                {/* Mobile CTA — sits below cards so the bottom polaroid frames it */}
                {!compact && (
                    <div className="md:hidden relative z-10 flex justify-center items-center gap-3 mt-10 flex-wrap">
                        <Link
                            to="/book"
                            data-testid="reviews-book-table-btn-mobile"
                            className="btn-primary text-xs px-6 py-3 hover:!bg-[#EB4A18]"
                        >
                            Book a Table
                            <span aria-hidden="true">→</span>
                        </Link>
                        <Link
                            to="/vouchers"
                            data-testid="reviews-gift-voucher-btn-mobile"
                            className="text-xs px-6 py-3 border border-[#121212] text-[#121212] uppercase tracking-[0.18em] font-medium hover:bg-[#121212] hover:text-[#FAF8F4] transition-colors inline-flex items-center gap-2"
                        >
                            Gift a Voucher
                            <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}

function Polaroid({ d, i, scrollYProgress, mobile = false }) {
    // Hooks must run before any early returns.
    const y = useTransform(scrollYProgress, [0, 1], [0, d.parallax]);
    const img = CAROUSEL_IMAGES[d.img];
    if (!img) return null;
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{
                opacity: mobile ? 0.78 : 0.92,
                scale: 1,
            }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{
                duration: 1.1,
                delay: d.delay,
                ease: [0.19, 1, 0.22, 1],
            }}
            style={{
                top: d.top,
                left: d.left,
                right: d.right,
                bottom: d.bottom,
                width: d.width,
                rotate: d.rotate,
                y,
                willChange: "transform",
            }}
            className="absolute"
        >
            {/* Polaroid frame */}
            <div
                className={`relative bg-white ${
                    mobile ? "p-2 pb-5" : "p-2.5 pb-7"
                } shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35),0_8px_20px_-8px_rgba(0,0,0,0.2)]`}
            >
                {/* Tape sticker */}
                {d.tape && (
                    <span
                        className={`absolute -top-2.5 left-1/2 -translate-x-1/2 ${
                            mobile
                                ? "px-2 py-0.5 text-[0.5rem]"
                                : "px-3 py-0.5 text-[0.6rem]"
                        } bg-[#EB4A18]/95 text-[#FAF8F4] uppercase tracking-[0.22em] font-extrabold rotate-[-3deg] shadow-sm whitespace-nowrap`}
                        aria-hidden="true"
                    >
                        {d.tape}
                    </span>
                )}
                <div className="relative aspect-square overflow-hidden">
                    <img
                        src={cdnImg(img.url, 500)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>
        </motion.div>
    );
}

// Reusable polaroid frame for inline (in-flow) use on mobile.
function InlinePolaroidFrame({ imgUrl, width, rotate, tape, animIdx = 0 }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95, rotate: 0 }}
            whileInView={{ opacity: 1, y: 0, scale: 1, rotate }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
                duration: 0.9,
                ease: [0.19, 1, 0.22, 1],
            }}
            style={{ width }}
            className="relative shrink-0"
        >
            <div
                className="relative bg-white p-2 pb-5 shadow-[0_28px_50px_-22px_rgba(0,0,0,0.4),0_10px_18px_-10px_rgba(0,0,0,0.25)]"
            >
                {tape && (
                    <span
                        className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-[#EB4A18]/95 text-[#FAF8F4] text-[0.55rem] uppercase tracking-[0.22em] font-extrabold rotate-[-3deg] shadow-sm whitespace-nowrap"
                        aria-hidden="true"
                    >
                        {tape}
                    </span>
                )}
                <div className="relative aspect-square overflow-hidden">
                    <img
                        src={cdnImg(imgUrl, 400)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>
        </motion.div>
    );
}

// Pair of overlapping polaroids placed above the first mobile review card.
function InlinePair({ pair }) {
    const leftImg = CAROUSEL_IMAGES[pair.left.img];
    const rightImg = CAROUSEL_IMAGES[pair.right.img];
    if (!leftImg || !rightImg) return null;
    return (
        <div
            aria-hidden="true"
            className="relative flex items-end justify-center gap-[-30px] mb-8 pb-2"
            style={{ minHeight: 200 }}
        >
            <div className="-mr-6 translate-y-2">
                <InlinePolaroidFrame
                    imgUrl={leftImg.url}
                    width={pair.left.width}
                    rotate={pair.left.rotate}
                    tape={pair.left.tape}
                    animIdx={0}
                />
            </div>
            <div className="-ml-6 -translate-y-1">
                <InlinePolaroidFrame
                    imgUrl={rightImg.url}
                    width={pair.right.width}
                    rotate={pair.right.rotate}
                    tape={pair.right.tape}
                    animIdx={1}
                />
            </div>
        </div>
    );
}

function Stars({ small }) {
    const size = small ? "w-3.5 h-3.5" : "w-4 h-4";
    return (
        <span className="inline-flex items-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
                <svg
                    key={i}
                    viewBox="0 0 24 24"
                    className={`${size} fill-[#EB4A18]`}
                    aria-hidden="true"
                >
                    <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18.2 22 12 18l-6.2 4 1.7-7.2L2 10l7.1-1.1L12 2z" />
                </svg>
            ))}
        </span>
    );
}

function GoogleG() {
    return (
        <svg viewBox="0 0 18 18" aria-hidden="true" className="w-3 h-3">
            <path
                fill="#4285F4"
                d="M16.51 8.18c0-.51-.05-1.02-.13-1.51H9v2.87h4.21a3.6 3.6 0 0 1-1.57 2.36v1.97h2.54c1.49-1.37 2.33-3.4 2.33-5.69z"
            />
            <path
                fill="#34A853"
                d="M9 17c2.13 0 3.92-.7 5.22-1.92l-2.54-1.97c-.71.48-1.61.76-2.68.76-2.06 0-3.81-1.39-4.43-3.26H1.94v2.04A8 8 0 0 0 9 17z"
            />
            <path
                fill="#FBBC05"
                d="M4.57 10.61a4.8 4.8 0 0 1 0-3.06V5.51H1.94a8 8 0 0 0 0 6.94l2.63-1.84z"
            />
            <path
                fill="#EA4335"
                d="M9 4.58c1.16 0 2.2.4 3.02 1.18l2.26-2.26A8 8 0 0 0 1.94 5.51l2.63 2.04C5.19 5.97 6.94 4.58 9 4.58z"
            />
        </svg>
    );
}
